// src/pages/Lounge/ExploreCitySingle/ExploreCitySingle.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import CityImage from "@/assets/UI/city.webp";
import api from "@/utils/axios";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";

import cls from "./ExploreCitySingle.module.scss";

const LANGS = ["en", "si", "ta", "hi"];

const languageLabel = (code) => {
  const map = {
    en: "English",
    si: "සිංහල (Sinhala)",
    ta: "தமிழ் (Tamil)",
    hi: "हिन्दी (Hindi)",
  };
  return map[code] || code?.toUpperCase() || "";
};

const statusLabel = (status) => {
  if (status === "open") return "Open";
  if (status === "closed") return "Closed";
  return status || "";
};

const getImageFromSlot = (slot) => {
  if (!slot) return null;
  if (typeof slot === "string") return slot;
  if (typeof slot === "object") return slot.url || slot.rawUrl || null;
  return null;
};

const ExploreCitySingle = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { cityId } = useParams();

  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [languageFilter, setLanguageFilter] = useState("all");

  // Same token pattern as AdminCitySingle
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  // Tabs active state
  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  useEffect(() => {
    let cancelled = false;

    const loadCity = async () => {
      if (!cityId) return;
      try {
        setLoading(true);
        setErr("");
        const { data } = await api.get(`/v1/lounge/cities/explore/${cityId}`);
        if (!cancelled) setCity(data || null);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            t("exploreCitySingle.error.generic");
          setErr(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCity();
    return () => {
      cancelled = true;
    };
  }, [cityId, t]);

  const avatarUrl = useMemo(() => {
    if (!city) return null;

    const thumb = getImageFromSlot(city.avatarThumb);
    if (thumb) return thumb;

    const avatar = getImageFromSlot(city.avatar);
    if (avatar) return avatar;

    return CityImage;
  }, [city]);

  const coverUrl = useMemo(() => {
    if (!city) return null;
    const cover = getImageFromSlot(city.coverImage);
    if (cover) return cover;
    return CityImage;
  }, [city]);

  const videoId = useMemo(() => {
    if (!city) return "";
    if (city.cityVideoId && typeof city.cityVideoId === "string") {
      return city.cityVideoId;
    }
    if (
      city.cityVideoId &&
      typeof city.cityVideoId === "object" &&
      city.cityVideoId._id
    ) {
      return city.cityVideoId._id;
    }
    if (city.cityVideo && city.cityVideo.videoId) return city.cityVideo.videoId;
    if (city.cityVideo && city.cityVideo._id) return city.cityVideo._id;
    return "";
  }, [city]);

  const hasVideo = Boolean(videoId);

  const posterUrl = useMemo(() => {
    if (!city) return null;
    return (
      getImageFromSlot(city.avatar) ||
      getImageFromSlot(city.avatarThumb) ||
      CityImage
    );
  }, [city]);

  const openStreets = useMemo(() => {
    if (!city?.streets) return [];
    return city.streets.filter((s) => s.streetStatus === "open");
  }, [city]);

  const closedStreets = useMemo(() => {
    if (!city?.streets) return [];
    return city.streets.filter((s) => s.streetStatus === "closed");
  }, [city]);

  const availableLanguages = useMemo(() => {
    const codes = new Set(
      openStreets
        .map((s) => s.streetLanguage)
        .filter((code) => typeof code === "string" && code.length > 0),
    );
    return Array.from(codes);
  }, [openStreets]);

  const filteredOpenStreets = useMemo(() => {
    if (languageFilter === "all") return openStreets;
    return openStreets.filter((s) => s.streetLanguage === languageFilter);
  }, [openStreets, languageFilter]);

  const getStreetImage = (street) => {
    const src = getImageFromSlot(street.avatarThumb);
    return src || CityImage;
  };

  const renderStreetCard = (street) => {
    const imageSrc = getStreetImage(street);
    const bodyIntro = street.streetIntro || "";
    const language = languageLabel(street.streetLanguage);
    const status = statusLabel(street.streetStatus);

    const metaText = t("exploreCitySingle.streets.meta", { language, status });

    const isClosed = street.streetStatus !== "open";

    return (
      <Motion.div
        key={street.streetId}
        whileHover={{ y: -4, scale: 1.02 }}
        transition={{ duration: 0.16 }}
      >
        <div className={cls.clubCard}>
          <div className={cls.clubImageWrap}>
            <img
              src={imageSrc}
              alt={street.streetName}
              className={cls.clubImage}
            />
          </div>

          <div className={cls.clubBody}>
            <h3 className={cls.clubName}>{street.streetName}</h3>
            <p className={cls.clubMeta}>{metaText}</p>
            {bodyIntro && <p className={cls.clubIntro}>{bodyIntro}</p>}
          </div>

          <div className={cls.clubFooter}>
            {isClosed ? (
              <button
                type="button"
                className={`${cls.clubVisitBtn} ${cls.clubVisitBtnDisabled}`}
                disabled
              >
                {t("exploreCitySingle.streets.closedBtn")}
              </button>
            ) : (
              <Link
                to={`/lounge/streets/single/${street.streetId}`}
                className={cls.clubVisitBtn}
              >
                {t("exploreCitySingle.streets.viewBtn")}
              </Link>
            )}
          </div>
        </div>
      </Motion.div>
    );
  };

  // ✅ Loader gate AFTER hooks (safe, no hook mismatch)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  // Error / not found (after loading is done)
  if (!city || err) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <p className={cls.errorText}>
            {err || t("exploreCitySingle.error.notFound")}
          </p>
        </div>
      </div>
    );
  }

  const cityLanguage = languageLabel(city.cityLanguage);
  const cityStatus = statusLabel(city.cityStatus);

  const heroMeta = t("exploreCitySingle.hero.meta", {
    language: cityLanguage,
    status: cityStatus,
  });

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* CTA row */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h7v-3.5c0-1.19.41-2.27 1.09-3.21C8.61 13.11 8.31 13 8 13zm8 0c-.32 0-.62.11-.91.17.68.94 1.09 2.02 1.09 3.33V20h7v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 15.19C14.74 18.64 13.02 19 12 19c-2.76 0-5-2.24-5-5 0-1.02.36-2.74 1.81-4.19C10.26 8.36 11.98 8 13 8c2.76 0 5 2.24 5 5 0 1.02-.36 2.74-1.81 4.19z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C8.01 14 6 11.99 6 9.5S8.01 5 10.5 5 15 7.01 15 9.5 12.99 14 10.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* HERO */}
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                <Motion.div
                  className={cls.heroFrame}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div
                    className={cls.heroCover}
                    style={
                      coverUrl
                        ? { backgroundImage: `url(${coverUrl})` }
                        : undefined
                    }
                  >
                    <div className={cls.heroOverlay} />
                    <div className={cls.heroContentRow}>
                      <div className={cls.heroAvatarWrap}>
                        <div className={cls.heroAvatarGlow}>
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={city.cityName}
                              className={cls.heroAvatarImage}
                            />
                          ) : (
                            <div className={cls.heroAvatarFallback}>
                              {city.cityName?.[0] || "C"}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={cls.heroText}>
                        <span className={cls.heroLabel}>
                          {t("exploreCitySingle.hero.label")}
                        </span>
                        <h1 className={cls.heroTitle}>{city.cityName}</h1>
                        {city.cityIntro && (
                          <p className={cls.heroIntro}>{city.cityIntro}</p>
                        )}
                        <p className={cls.heroMeta}>{heroMeta}</p>
                      </div>
                    </div>
                  </div>
                </Motion.div>

                <Motion.div
                  className={cls.heroCard}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.1 }}
                >
                  <span className={cls.searchPromptLabel}>
                    {t("exploreCitySingle.hero.cardLabel")}
                  </span>
                  <h2 className={cls.searchPromptTitle}>
                    {t("exploreCitySingle.hero.cardTitle", {
                      cityName: city.cityName,
                    })}
                  </h2>
                  <p className={cls.searchPromptText}>
                    {city.cityDescription ||
                      t("exploreCitySingle.hero.cardTextFallback")}
                  </p>
                </Motion.div>
              </div>
            </section>

            {/* VIDEO */}
            <section className={cls.videoSection}>
              <div className={cls.card}>
                <div className={cls.cardHeaderRow}>
                  <h2 className={cls.cardTitle}>
                    {t("exploreCitySingle.video.cardTitle")}
                  </h2>
                  <p className={cls.cardMeta}>
                    {t("exploreCitySingle.video.cardSubtitle")}
                  </p>
                </div>

                <div className={cls.videoPlayerWrap}>
                  {hasVideo ? (
                    <VideoPlayer
                      token={token}
                      videoId={videoId}
                      poster={posterUrl}
                      mode="proxy"
                      height={220}
                    />
                  ) : (
                    <div className={cls.noVideo}>
                      {t("exploreCitySingle.video.noVideo")}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* STREETS */}
            <section className={cls.clubsGridSection}>
              <div className={cls.clubsGridHeader}>
                <h2 className={cls.cityListTitle}>
                  {t("exploreCitySingle.streets.openTitle")}
                </h2>
                <p className={cls.cityListMeta}>
                  {t("exploreCitySingle.streets.openMeta")}
                </p>
              </div>

              <div className={cls.filtersRow}>
                <span className={cls.filterLabel}>
                  {t("exploreCitySingle.filters.languageLabel")}
                </span>
                <select
                  className={cls.filterSelect}
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                >
                  <option value="all">
                    {t("exploreCitySingle.filters.allLanguages")}
                  </option>
                  {availableLanguages.map((code) => (
                    <option key={code} value={code}>
                      {languageLabel(code)}
                    </option>
                  ))}
                </select>
              </div>

              {filteredOpenStreets.length === 0 ? (
                <p className={cls.errorText}>
                  {t("exploreCitySingle.streets.noOpen")}
                </p>
              ) : (
                <div className={cls.clubsGrid}>
                  {filteredOpenStreets.map((s) => renderStreetCard(s))}
                </div>
              )}

              {closedStreets.length > 0 && (
                <div className={cls.closedSection}>
                  <div className={cls.clubsGridHeader}>
                    <h2 className={cls.cityListTitle}>
                      {t("exploreCitySingle.streets.closedTitle")}
                    </h2>
                    <p className={cls.cityListMeta}>
                      {t("exploreCitySingle.streets.closedMeta")}
                    </p>
                  </div>

                  <div className={cls.clubsGrid}>
                    {closedStreets.map((s) => renderStreetCard(s))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreCitySingle;
